/**
 * The Help & feedback panel's one voice input (SA-1310 spec §8): Deepgram when the `voice` feature is
 * loaded and a Deepgram key is there, else the browser's speech recognition, else none (no mic).
 * Words reach `onFinal` once listening ends; they are never sent on their own.
 */
import { useCallback } from 'react'
import { usePushToTalkSTT } from '@/hooks/usePushToTalkSTT'
import { useBrowserSpeech } from '@/hooks/useBrowserSpeech'
import { useFeatureStore, useSettingsStore } from '@/stores'
import { resolveSttLanguage } from '@/utils/sttLanguageResolver'

export type VoiceEngine = 'deepgram' | 'browser' | 'none'

const DEEPGRAM_DENIED = 'Microphone access denied'

export function useFeedbackVoice({ onFinal }: { onFinal: (text: string) => void }) {
  const voiceLoaded = useFeatureStore((s) => s.isLoaded && s.features.has('voice'))
  const hasDeepgramKey = useSettingsStore((s) => s.hasDeepgramKey)
  const userSttLanguage = useSettingsStore((s) => s.voiceConversation.sttLanguage)
  const language = resolveSttLanguage({ userSttLanguage })

  const deepgram = usePushToTalkSTT({ onTranscript: onFinal, language, utteranceEndMs: 2000 })
  const browser = useBrowserSpeech({ lang: language, onFinal })

  const engine: VoiceEngine = voiceLoaded && hasDeepgramKey && deepgram.isAvailable
    ? 'deepgram'
    : browser.supported ? 'browser' : 'none'

  const start = useCallback(() => {
    if (engine === 'deepgram') void deepgram.startListening()
    else if (engine === 'browser') browser.start()
  }, [engine, deepgram, browser])

  const stop = useCallback(() => {
    if (engine === 'deepgram') deepgram.stopListening()
    else if (engine === 'browser') browser.stop()
  }, [engine, deepgram, browser])

  if (engine === 'deepgram') {
    return {
      engine, start, stop,
      listening: deepgram.isListening,
      partial: deepgram.partialTranscript,
      denied: deepgram.error === DEEPGRAM_DENIED,
    }
  }
  return {
    engine, start, stop,
    listening: engine === 'browser' && browser.listening,
    partial: engine === 'browser' ? browser.partial : '',
    denied: engine === 'browser' && browser.denied,
  }
}
